import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement } from "react";
import {
  Field,
  FormPage,
  FormSection,
  MoneyInput,
  QtyInput,
  QueryStatus,
  ScaledInput,
  useToast,
} from "@cx/ui";
import type {
  ContactListItem,
  ListResult,
  SalesOrderCalc,
  SalesOrderFormOptions,
  SalesOrderView,
} from "@cx/contracts";
import {
  FREIGHT_MODES,
  FREIGHT_MODE_LABELS,
  formatBRL,
  formatDateBR,
  formatPct,
  todayInSaoPaulo,
  toPct4,
  type FreightMode,
} from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { TaxEstimatePanel } from "../fiscal/TaxEstimate";
import { ProductSearch } from "../prices/ProductSearch";
import {
  emptyOrderForm,
  formFromView,
  itemError,
  orderBody,
  previewBody,
  rowKey,
  type ItemRow,
  type SalesOrderForm,
} from "./labels";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/** Inclusão (/vendas/pedidos/novo) e edição (/vendas/pedidos/$id/editar) do pedido de venda. */
export function SalesOrderFormPage(): ReactElement {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const { companyId, api } = useSession();
  const options = useQuery({
    queryKey: ["sales-orders", companyId, "form-options"],
    queryFn: () => api<SalesOrderFormOptions>("/sales-orders/form-options"),
  });
  const order = useQuery({
    queryKey: ["sales-orders", companyId, "view", id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<SalesOrderView>(`/sales-orders/${id}`),
  });
  if (!options.data || (id && !order.data))
    return (
      <QueryStatus loading={options.isLoading || order.isLoading} error={options.error ?? order.error} />
    );
  if (order.data?.lockedReason)
    return (
      <div className="alert alert-warning" role="alert">
        {order.data.lockedReason}
      </div>
    );
  return <OrderFormBody key={id ?? "new"} options={options.data} order={order.data ?? null} />;
}

function OrderFormBody({ options, order }: { options: SalesOrderFormOptions; order: SalesOrderView | null }) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const onlySeller = options.sellerLocked ? (options.sellers[0]?.id ?? "") : "";
  const [f, setF] = useState<SalesOrderForm>(() =>
    order ? formFromView(order) : emptyOrderForm(todayInSaoPaulo(), onlySeller),
  );
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Uma chave por tela de inclusão: clique duplo ou repetição da rede não criam dois pedidos.
  const [idemKey] = useState(() => crypto.randomUUID());
  const set = <K extends keyof SalesOrderForm>(k: K, v: SalesOrderForm[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };
  const setItem = (key: string, patch: Partial<ItemRow>) => {
    setF((x) => ({ ...x, items: x.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
    setDirty(true);
  };

  const pb = previewBody(f);
  const preview = useQuery({
    queryKey: ["sales-orders", companyId, "preview", pb],
    enabled: !!pb,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: () => api<SalesOrderCalc>("/sales-orders/preview", { method: "POST", body: JSON.stringify(pb) }),
  });
  const calc = pb ? preview.data : undefined;

  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(orderBody(f));
      if (order) {
        await api(`/sales-orders/${order.id}`, { method: "PUT", body });
        return order.id;
      }
      const r = await api<{ id: string }>("/sales-orders", {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idemKey },
      });
      return r.id;
    },
    onSuccess: (savedId) => {
      toast("success", "Pedido salvo");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
      navigate({ to: "/vendas/pedidos/$id", params: { id: savedId } });
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      if (e instanceof ApiFailure && e.code === "invalid_contact") setErrors({ contactId: e.message });
      toast("danger", errorMessage(e));
    },
  });

  const title = order ? `Pedido de venda nº ${order.number}` : "Novo pedido de venda";
  const back = () =>
    order
      ? navigate({ to: "/vendas/pedidos/$id", params: { id: order.id } })
      : navigate({ to: "/vendas/pedidos" });
  const previewError = pb && preview.error ? errorMessage(preview.error) : null;
  const newItem = (p: Partial<ItemRow>): ItemRow => ({
    key: rowKey(),
    productId: null,
    description: "",
    code: null,
    unit: "",
    qtyQ4: 10_000,
    listPriceCents: null,
    discountPct4: null,
    warehouseId: null,
    ...p,
  });

  return (
    <FormPage
      title={title}
      breadcrumb={["Vendas", "Pedidos de venda", order ? String(order.number) : "Novo"]}
      onSave={() => save.mutate()}
      onCancel={back}
      saving={save.isPending}
      dirty={dirty}
      aside={
        // Totais e impostos estimados ficam juntos à vista ao rolar os itens.
        <div className="position-sticky" style={{ top: "1rem" }}>
          <TotalsBox calc={calc} error={previewError} loading={preview.isFetching} />
          <TaxEstimatePanel
            contactId={f.contact?.id ?? null}
            lines={(calc?.items ?? []).map((i) => ({
              productId: i.productId,
              qtyQ4: i.qtyQ4,
              unitPriceCents: i.listPriceCents,
              totalCents: i.totalCents,
            }))}
            discountCents={calc?.discountCents ?? 0}
            freightCents={calc?.freightCents ?? 0}
            otherExpensesCents={calc?.otherExpensesCents ?? 0}
          />
        </div>
      }
    >
      <FormSection title="Cliente e pedido">
        <div className="row">
          <div className="col-md-8 mb-3">
            <ContactPicker value={f.contact} error={errors.contactId} onChange={(c) => set("contact", c)} />
          </div>
          <Field label="Pedido de compra do cliente" className="col-md-4" error={errors.customerPo}>
            <input value={f.customerPo} maxLength={60} onChange={(e) => set("customerPo", e.target.value)} />
          </Field>
          <Field label="Data" required className="col-md-3" error={errors.date}>
            <input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
          </Field>
          <Field label="Data prevista" className="col-md-3" error={errors.expectedDate}>
            <input type="date" value={f.expectedDate} onChange={(e) => set("expectedDate", e.target.value)} />
          </Field>
          <Field label="Vendedor" className="col-md-3" error={errors.sellerId}>
            <select
              className="form-select"
              value={f.sellerId}
              disabled={options.sellerLocked}
              onChange={(e) => set("sellerId", e.target.value)}
            >
              {!options.sellerLocked && <option value="">Sem vendedor</option>}
              {options.sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Depósito" className="col-md-3" error={errors.warehouseId}>
            <select
              className="form-select"
              value={f.warehouseId}
              onChange={(e) => set("warehouseId", e.target.value)}
            >
              <option value="">Padrão da empresa</option>
              {options.warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Lista de preços"
            className="col-md-6"
            error={errors.priceListId}
            hint="Ao trocar a lista, os preços dos produtos são recalculados."
          >
            <select
              className="form-select"
              value={f.priceListId}
              onChange={(e) => {
                const v = e.target.value;
                setF((x) => ({
                  ...x,
                  priceListId: v,
                  items: x.items.map((i) => (i.productId ? { ...i, listPriceCents: null } : i)),
                }));
                setDirty(true);
              }}
            >
              <option value="">Preço de venda do produto</option>
              {order?.priceList && !options.priceLists.some((l) => l.id === order.priceList!.id) && (
                <option value={order.priceList.id}>{order.priceList.name}</option>
              )}
              {options.priceLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Condição de pagamento" className="col-md-6" error={errors.paymentTermId}>
            <select
              className="form-select"
              value={f.paymentTermId}
              onChange={(e) => set("paymentTermId", e.target.value)}
            >
              <option value="">Sem parcelas</option>
              {order?.paymentTerm && !options.paymentTerms.some((t) => t.id === order.paymentTerm!.id) && (
                <option value={order.paymentTerm.id}>{order.paymentTerm.name}</option>
              )}
              {options.paymentTerms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </FormSection>

      <FormSection title="Itens">
        {errors.items && <div className="alert alert-danger py-2">{errors.items}</div>}
        <ItemsTable
          f={f}
          calc={calc}
          errors={errors}
          setItem={setItem}
          onRemove={(key) =>
            set(
              "items",
              f.items.filter((i) => i.key !== key),
            )
          }
        />
        <div className="row align-items-end">
          <div className="col-md-8">
            <ProductSearch
              id="order-product"
              label="Adicionar produto"
              action="Adicionar"
              onPick={(p) =>
                set("items", [
                  ...f.items,
                  newItem({ productId: p.id, description: p.name, code: p.sku, unit: p.unit ?? "" }),
                ])
              }
            />
          </div>
          {options.canAddOtherItems && (
            <div className="col-md-4 mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => set("items", [...f.items, newItem({})])}
              >
                Adicionar outro item
              </button>
            </div>
          )}
        </div>
      </FormSection>

      <FormSection title="Descontos, frete e despesas">
        <div className="row">
          <fieldset className="col-md-6 mb-3">
            <legend className="form-label fs-6">Desconto geral</legend>
            {(
              [
                ["value", "Em valor"],
                ["pct", "Em percentual"],
              ] as const
            ).map(([m, label]) => (
              <div key={m} className="form-check form-check-inline">
                <input
                  id={`order-discount-${m}`}
                  type="radio"
                  name="order-discount-mode"
                  className="form-check-input"
                  checked={f.discountMode === m}
                  onChange={() => set("discountMode", m)}
                />
                <label className="form-check-label" htmlFor={`order-discount-${m}`}>
                  {label}
                </label>
              </div>
            ))}
          </fieldset>
          {f.discountMode === "value" ? (
            <Field label="Desconto" className="col-md-6" error={errors.discountCents}>
              <MoneyInput value={f.discountCents} onChange={(v) => set("discountCents", v)} />
            </Field>
          ) : (
            <Field label="Desconto (%)" className="col-md-6" error={errors.discountPct4}>
              <ScaledInput
                value={f.discountPct4}
                parse={toPct4}
                format={pctNumber}
                suffix="%"
                onChange={(v) => set("discountPct4", v)}
              />
            </Field>
          )}
          <Field label="Frete" className="col-md-6" error={errors.freightCents}>
            <MoneyInput value={f.freightCents} onChange={(v) => set("freightCents", v)} />
          </Field>
          <Field label="Outras despesas" className="col-md-6" error={errors.otherExpensesCents}>
            <MoneyInput value={f.otherExpensesCents} onChange={(v) => set("otherExpensesCents", v)} />
          </Field>
        </div>
        {calc && calc.installments.length > 0 && (
          <table className="table table-sm w-auto" aria-label="Parcelas">
            <thead>
              <tr>
                <th scope="col">Parcela</th>
                <th scope="col">Vencimento</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {calc.installments.map((p) => (
                <tr key={p.number}>
                  <td>{p.number}</td>
                  <td>{formatDateBR(p.dueDate)}</td>
                  <td className="text-end">{formatBRL(p.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FormSection>

      <FormSection
        title="Transporte e observações"
        defaultOpen={!!(order?.carrier || order?.notes || order?.internalNotes)}
      >
        <div className="row">
          <Field label="Transportador" className="col-md-6" error={errors.carrierId}>
            <select
              className="form-select"
              value={f.carrierId}
              onChange={(e) => set("carrierId", e.target.value)}
            >
              <option value="">Nenhum</option>
              {options.carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Modalidade do frete" className="col-md-6">
            <select
              className="form-select"
              value={f.freightMode}
              onChange={(e) => set("freightMode", Number(e.target.value) as FreightMode)}
            >
              {FREIGHT_MODES.map((m) => (
                <option key={m} value={m}>
                  {FREIGHT_MODE_LABELS[m]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Observações" className="col-12" hint="Saem no PDF do pedido." error={errors.notes}>
            <textarea
              rows={3}
              maxLength={5000}
              value={f.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
          <Field label="Observações internas" className="col-12" error={errors.internalNotes}>
            <textarea
              rows={2}
              maxLength={5000}
              value={f.internalNotes}
              onChange={(e) => set("internalNotes", e.target.value)}
            />
          </Field>
        </div>
      </FormSection>
    </FormPage>
  );
}

function ItemsTable({
  f,
  calc,
  errors,
  setItem,
  onRemove,
}: {
  f: SalesOrderForm;
  calc: SalesOrderCalc | undefined;
  errors: Record<string, string>;
  setItem(key: string, patch: Partial<ItemRow>): void;
  onRemove(key: string): void;
}) {
  if (f.items.length === 0) return <p className="text-muted">Nenhum item. Busque um produto abaixo.</p>;
  return (
    <div className="table-responsive mb-3">
      <table className="table align-middle" aria-label="Itens do pedido">
        <thead>
          <tr>
            <th scope="col">Descrição</th>
            <th scope="col" style={{ width: "5rem" }}>
              Un
            </th>
            <th scope="col" style={{ width: "7rem" }} className="text-end">
              Qtd
            </th>
            <th scope="col" style={{ width: "10rem" }} className="text-end">
              Preço un.
            </th>
            <th scope="col" style={{ width: "7rem" }} className="text-end">
              Desc. %
            </th>
            <th scope="col" className="text-end">
              Total
            </th>
            <th scope="col">
              <span className="visually-hidden">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {f.items.map((it, i) => {
            const line = calc?.items[i];
            const err = itemError(errors, i);
            const name = it.description || `item ${i + 1}`;
            return (
              <tr key={it.key}>
                <td>
                  {it.productId ? (
                    <>
                      {it.description}
                      {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                    </>
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      aria-label={`Descrição do item ${i + 1}`}
                      maxLength={1000}
                      value={it.description}
                      onChange={(e) => setItem(it.key, { description: e.target.value })}
                    />
                  )}
                  {err && <div className="invalid-feedback d-block">{err}</div>}
                </td>
                <td>
                  {it.productId ? (
                    it.unit || "—"
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      aria-label={`Unidade do item ${i + 1}`}
                      maxLength={6}
                      value={it.unit}
                      onChange={(e) => setItem(it.key, { unit: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  <QtyInput
                    aria-label={`Quantidade de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.qtyQ4}
                    onChange={(v) => setItem(it.key, { qtyQ4: v })}
                  />
                </td>
                <td>
                  <MoneyInput
                    aria-label={`Preço unitário de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.listPriceCents ?? line?.listPriceCents ?? null}
                    onChange={(v) => setItem(it.key, { listPriceCents: v })}
                  />
                </td>
                <td>
                  <ScaledInput
                    aria-label={`Desconto de ${name}`}
                    className="form-control form-control-sm text-end"
                    value={it.discountPct4}
                    parse={toPct4}
                    format={pctNumber}
                    onChange={(v) => setItem(it.key, { discountPct4: v })}
                  />
                </td>
                <td className="text-end text-nowrap">{line ? formatBRL(line.totalCents) : "—"}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remover ${name}`}
                    onClick={() => onRemove(it.key)}
                  >
                    Remover
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TotalsBox({
  calc,
  error,
  loading,
}: {
  calc: SalesOrderCalc | undefined;
  error: string | null;
  loading: boolean;
}) {
  const rows: [string, number][] = calc
    ? [
        ["Total dos itens", calc.itemsTotalCents],
        ["Desconto", -calc.discountCents],
        ["Frete", calc.freightCents],
        ["Outras despesas", calc.otherExpensesCents],
      ]
    : [];
  return (
    <div className="box card p-3 mb-3" aria-live="polite">
      <h2 className="h6">Totais</h2>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {!calc && !error && <p className="text-muted small mb-0">Escolha o cliente e inclua itens.</p>}
      {calc && (
        <dl className="row mb-0 small" aria-busy={loading}>
          {rows.map(([label, v]) => (
            <Fragment key={label}>
              <dt className="col-7 fw-normal">{label}</dt>
              <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
            </Fragment>
          ))}
          <dt className="col-7">Total do pedido</dt>
          <dd className="col-5 text-end fw-bold mb-1" data-testid="order-total">
            {formatBRL(calc.totalCents)}
          </dd>
          {/* Sem sellers:read a API não manda a comissão (o usuário-vendedor vê a dele). */}
          {(calc.commissionTotalCents ?? 0) > 0 && (
            <>
              <dt className="col-7 fw-normal">Comissão</dt>
              <dd className="col-5 text-end mb-0">{formatBRL(calc.commissionTotalCents ?? 0)}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

/** Cliente do pedido: busca por nome ou CPF/CNPJ (usuário-vendedor só acha a própria carteira). */
export function ContactPicker({
  value,
  error,
  onChange,
}: {
  value: { id: string; name: string } | null;
  error?: string;
  onChange(c: { id: string; name: string } | null): void;
}) {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useQuery({
    queryKey: ["contacts", companyId, { q: term, picker: "sales-order" }],
    enabled: !value && term.length >= 2,
    queryFn: () =>
      api<ListResult<ContactListItem>>(`/contacts?${new URLSearchParams({ q: term, limit: "8" })}`),
  });
  if (value)
    return (
      <div>
        <span className="form-label d-block">
          Cliente{" "}
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        </span>
        <p className="mb-0" data-testid="order-contact">
          <strong>{value.name}</strong>{" "}
          <button type="button" className="btn btn-sm btn-link" onClick={() => onChange(null)}>
            Trocar
          </button>
        </p>
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    );
  return (
    <div>
      <label className="form-label" htmlFor="order-contact-search">
        Cliente{" "}
        <span className="text-danger" aria-hidden="true">
          *
        </span>
      </label>
      <input
        id="order-contact-search"
        type="search"
        className={`form-control${error ? " is-invalid" : ""}`}
        placeholder="Nome ou CPF/CNPJ"
        aria-invalid={error ? "true" : undefined}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
      {term.length >= 2 && (
        <ul className="list-group mt-1" aria-label="Clientes encontrados">
          {found.isSuccess && found.data.data.length === 0 && (
            <li className="list-group-item text-muted">Nenhum contato encontrado.</li>
          )}
          {found.data?.data.map((c) => (
            <li key={c.id} className="list-group-item d-flex align-items-center gap-2">
              <span className="flex-grow-1">{c.name}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`Escolher ${c.name}`}
                onClick={() => {
                  onChange({ id: c.id, name: c.name });
                  setQ("");
                }}
              >
                Escolher
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
